import { createReducer } from '@reduxjs/toolkit';
import { ClientDto, ProjectDto, SiteDto } from '../../api/ClientApi';

import {
    importClientForClientDetails,
    importProjectsForClientDetails,
    clearClientForClientDetails,
    removeClientForClientDetails,
    removeProjectForClientDetails,
    updateClientForClientDetails,
    updateProjectForClientDetails,
    addNewProjectForClientDetails,
    saveClient,
} from '../actions/clientActions';
import { saveSite } from '../actions/siteActions';

export interface ClientDetailsReducerState {
    client: ClientDto | null;
    projects: ProjectDto[] | null;
}

function clearState(state: ClientDetailsReducerState): void {
    state.client = null;
    state.projects = null;
}

function getInitialState(): ClientDetailsReducerState {
    const state: ClientDetailsReducerState = {
        client: null,
        projects: null,
    };

    clearState(state);

    return state;
}

export const clientDetailsReducer = createReducer<ClientDetailsReducerState>(
    getInitialState(), // Initial state
    (builder) => {
        builder
            .addCase(importClientForClientDetails.fulfilled, (state: ClientDetailsReducerState, action: any) => {
                const newClient = action.payload as ClientDto;

                state.client = newClient;
            })
            .addCase(importProjectsForClientDetails.fulfilled, (state: ClientDetailsReducerState, action: any) => {
                const projects = action.payload as ProjectDto[];

                state.projects = projects;
            })
            .addCase(addNewProjectForClientDetails, (state: ClientDetailsReducerState, action: any) => {
                const project = action.payload as ProjectDto;

                state.projects = [...(state.projects ?? []), project];
            })
            .addCase(clearClientForClientDetails, (state: ClientDetailsReducerState, action: any) => {
                clearState(state);
            })
            .addCase(updateClientForClientDetails, (state: ClientDetailsReducerState, action: any) => {
                const newClient = action.payload as ClientDto;

                if (newClient.id === state.client?.id) {
                    state.client = newClient;
                }
            })
            .addCase(updateProjectForClientDetails, (state: ClientDetailsReducerState, action: any) => {
                const newProject = action.payload as ProjectDto;

                const projectIndex = state.projects?.findIndex((p) => p.id === newProject.id);

                if (projectIndex !== undefined && projectIndex > -1) {
                    if (!!state.projects) {
                        state.projects = [
                            ...state.projects?.slice(0, projectIndex),
                            newProject,
                            ...state.projects?.slice(projectIndex + 1),
                        ];
                    } else {
                        state.projects = [newProject];
                    }
                }
            })
            .addCase(removeClientForClientDetails, (state: ClientDetailsReducerState, action: any) => {
                state.client = null;
            })
            .addCase(removeProjectForClientDetails, (state: ClientDetailsReducerState, action: any) => {
                const oldProjectId = action.payload as number;

                const projectIndex = state.projects?.findIndex((p) => p.id === oldProjectId);

                if (projectIndex !== undefined && projectIndex > -1) {
                    if (!!state.projects) {
                        state.projects.splice(projectIndex, 1);
                    } else {
                        state.projects = [];
                    }
                }
            })
            .addCase(saveSite.fulfilled, (state: ClientDetailsReducerState, action: any) => {
                const site = action.payload as SiteDto;

                const projectIndex = state.projects?.findIndex((p) => p.id === site.projectId);

                if (projectIndex !== undefined && projectIndex > -1) {
                    if (!!state.projects) {
                        const project = state.projects[projectIndex];

                        if (!!project.sites) {
                            const siteIndex = project.sites!.findIndex((s) => s.id === site.id);

                            if (siteIndex >= 0) {
                                project.sites?.splice(siteIndex, 1, site);
                            } else {
                                project.sites?.unshift(site);
                            }
                            state.projects.splice(projectIndex, 1, project);
                        }
                    } else {
                        state.projects = [];
                    }
                }
            })
            .addCase(saveClient.fulfilled, (state: ClientDetailsReducerState, action: any) => {
                const client = action.payload as ClientDto;

                state.client = client;
            });
    },
);
